import { useCallback, useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IMessageCategory, IMessageOfTheDay } from "@dc-ireland/shared"
import ImagePicker from "../../components/admin/ImagePicker"
import AdminAlerts from "../../components/admin/AdminAlerts"

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

const emptyMessageForm = {
  title: "",
  message: "",
}

const emptyCategoryForm = {
  name: "",
  slug: "",
  backgroundUrl: "",
}

const AdminMessage = () => {
  const [categories, setCategories] = useState<IMessageCategory[]>([])
  const [selectedSlug, setSelectedSlug] = useState<string>("")
  const [categoryForm, setCategoryForm] = useState({ name: "", backgroundUrl: "" })
  const [messages, setMessages] = useState<IMessageOfTheDay[]>([])
  const [messageForm, setMessageForm] = useState(emptyMessageForm)
  const [newCategoryForm, setNewCategoryForm] = useState(emptyCategoryForm)
  const [loading, setLoading] = useState(true)
  const [savingCategory, setSavingCategory] = useState(false)
  const [savingMessage, setSavingMessage] = useState(false)
  const [creatingCategory, setCreatingCategory] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const loadCategories = useCallback(async () => {
    const data = await apiClient.getMessageCategories()
    setCategories(data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
    return data
  }, [])

  const loadCategoryFeed = useCallback(async (slug: string) => {
    const feed = await apiClient.getMessageCategory(slug, true)
    if (feed) {
      setCategoryForm({
        name: feed.category.name,
        backgroundUrl: feed.category.backgroundUrl,
      })
      setMessages(feed.messages ?? [])
    }
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const data = await loadCategories()
        const initialSlug = data[0]?.slug ?? ""
        setSelectedSlug(initialSlug)
        if (initialSlug) await loadCategoryFeed(initialSlug)
      } catch (err) {
        console.error(err)
        setError("Failed to load message categories")
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [loadCategories, loadCategoryFeed])

  const selectCategory = async (slug: string) => {
    setSelectedSlug(slug)
    setError(null)
    setSuccess(null)
    setMessageForm(emptyMessageForm)
    await loadCategoryFeed(slug)
  }

  const handleSaveCategory = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedSlug) return

    setSavingCategory(true)
    setError(null)
    setSuccess(null)
    try {
      await apiClient.updateMessageCategory(selectedSlug, categoryForm)
      await loadCategories()
      setSuccess("Category updated.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update category")
    } finally {
      setSavingCategory(false)
    }
  }

  const handleCreateCategory = async (e: FormEvent) => {
    e.preventDefault()
    setCreatingCategory(true)
    setError(null)
    setSuccess(null)
    try {
      const slug = newCategoryForm.slug.trim() || slugify(newCategoryForm.name)
      await apiClient.createMessageCategory({
        slug,
        name: newCategoryForm.name.trim(),
        backgroundUrl: newCategoryForm.backgroundUrl.trim(),
        sortOrder: categories.length,
      })
      setNewCategoryForm(emptyCategoryForm)
      const data = await loadCategories()
      const created = data.find((category) => category.slug === slug)
      if (created) await selectCategory(created.slug)
      setSuccess("Category created.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create category")
    } finally {
      setCreatingCategory(false)
    }
  }

  const handleCreateMessage = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedSlug) return

    setSavingMessage(true)
    setError(null)
    setSuccess(null)
    try {
      await apiClient.createMessageOfTheDay(selectedSlug, {
        title: messageForm.title.trim(),
        message: messageForm.message.trim(),
        sortOrder: messages.length,
        enabled: true,
      })
      setMessageForm(emptyMessageForm)
      await loadCategoryFeed(selectedSlug)
      await loadCategories()
      setSuccess("Message added.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add message")
    } finally {
      setSavingMessage(false)
    }
  }

  const handleDeleteMessage = async (id: string) => {
    if (!confirm("Delete this message?")) return
    setError(null)
    setSuccess(null)
    try {
      await apiClient.deleteMessageOfTheDayEntry(id)
      if (selectedSlug) await loadCategoryFeed(selectedSlug)
      await loadCategories()
      setSuccess("Message deleted.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete message")
    }
  }

  const moveMessage = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= messages.length) return

    const reordered = [...messages]
    ;[reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]]

    setMessages(reordered)
    try {
      await Promise.all(
        reordered.map((message, sortOrder) =>
          message.id
            ? apiClient.updateMessageOfTheDayEntry(message.id, { sortOrder })
            : Promise.resolve()
        )
      )
      if (selectedSlug) await loadCategoryFeed(selectedSlug)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reorder messages")
      if (selectedSlug) await loadCategoryFeed(selectedSlug)
    }
  }

  if (loading) {
    return <span className="loading loading-spinner loading-md"></span>
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Message of the Day</h2>
        <p className="mt-1 text-secondary">
          Manage categories and their messages. Each category has one shared background image.
        </p>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category.slug}
            type="button"
            className={`btn btn-sm ${selectedSlug === category.slug ? "btn-primary" : "btn-ghost"}`}
            onClick={() => void selectCategory(category.slug)}
          >
            {category.name}
            {typeof category.messageCount === "number" && (
              <span className="ml-1 opacity-70">({category.messageCount})</span>
            )}
          </button>
        ))}
      </div>

      {selectedSlug ? (
        <>
          <form onSubmit={handleSaveCategory} className="card bg-base-100 shadow-xl">
            <div className="card-body space-y-4">
              <h3 className="card-title text-lg">Category settings</h3>
              <label className="form-control">
                <span className="label-text">Category name</span>
                <input
                  className="input input-bordered"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                />
              </label>
              <ImagePicker
                label="Background image"
                value={categoryForm.backgroundUrl}
                onChange={(backgroundUrl) => setCategoryForm({ ...categoryForm, backgroundUrl })}
                hint="Shared by all messages in this category."
                required
              />
              <div className="card-actions justify-end">
                <button type="submit" className="btn btn-primary" disabled={savingCategory}>
                  {savingCategory ? "Saving…" : "Save Category"}
                </button>
              </div>
            </div>
          </form>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Messages in {categoryForm.name}</h3>

            {messages.length === 0 ? (
              <p className="text-secondary">No messages in this category yet.</p>
            ) : (
              messages.map((message, index) => (
                <div key={message.id} className="card bg-base-100 shadow-md">
                  <div className="card-body gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{message.title}</p>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-secondary">{message.message}</p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={index === 0}
                        onClick={() => void moveMessage(index, -1)}
                      >
                        Move up
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={index === messages.length - 1}
                        onClick={() => void moveMessage(index, 1)}
                      >
                        Move down
                      </button>
                      {message.id && (
                        <button
                          type="button"
                          className="btn btn-error btn-sm"
                          onClick={() => void handleDeleteMessage(message.id!)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleCreateMessage} className="card bg-base-100 shadow-xl">
            <div className="card-body space-y-4">
              <h3 className="card-title text-lg">Add message</h3>
              <label className="form-control">
                <span className="label-text">Title / reference</span>
                <input
                  className="input input-bordered"
                  value={messageForm.title}
                  onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                  placeholder="Philippians 4:6-7"
                  required
                />
              </label>
              <label className="form-control">
                <span className="label-text">Message</span>
                <textarea
                  className="textarea textarea-bordered"
                  rows={5}
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                />
              </label>
              <div className="card-actions justify-end">
                <button type="submit" className="btn btn-primary" disabled={savingMessage}>
                  {savingMessage ? "Adding…" : "Add Message"}
                </button>
              </div>
            </div>
          </form>
        </>
      ) : (
        <p className="text-secondary">Create a category to get started.</p>
      )}

      <form onSubmit={handleCreateCategory} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-4">
          <h3 className="card-title text-lg">Add category</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="form-control">
              <span className="label-text">Name</span>
              <input
                className="input input-bordered"
                value={newCategoryForm.name}
                onChange={(e) =>
                  setNewCategoryForm({
                    ...newCategoryForm,
                    name: e.target.value,
                    slug: newCategoryForm.slug || slugify(e.target.value),
                  })
                }
                placeholder="Touch of Heaven"
                required
              />
            </label>
            <label className="form-control">
              <span className="label-text">Slug</span>
              <input
                className="input input-bordered"
                value={newCategoryForm.slug}
                onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slug: slugify(e.target.value) })}
                placeholder="touch-of-heaven"
                required
              />
            </label>
            <div className="md:col-span-2">
              <ImagePicker
                label="Background image"
                value={newCategoryForm.backgroundUrl}
                onChange={(backgroundUrl) => setNewCategoryForm({ ...newCategoryForm, backgroundUrl })}
                required
              />
            </div>
          </div>
          <div className="card-actions justify-end">
            <button type="submit" className="btn btn-primary" disabled={creatingCategory}>
              {creatingCategory ? "Creating…" : "Create Category"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default AdminMessage
